import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";

const TIER_OPTIONS = [
  { id: "starter",    label: "RFQ Starter",       price: "$29/mo"  },
  { id: "growth",     label: "Catalogue Growth",  price: "$79/mo"  },
  { id: "leader",     label: "Category Leader",   price: "$199/mo" },
  { id: "enterprise", label: "Enterprise",        price: "$499/mo" },
];

function fmtCents(cents, currency = "usd") {
  const sign = cents < 0 ? "-" : "";
  const abs = Math.abs(cents) / 100;
  const cur = (currency || "usd").toUpperCase();
  return `${sign}${cur} ${abs.toFixed(2)}`;
}

/**
 * Iter61 · Change-plan flow with Stripe proration preview.
 * UX: pick target tier → fetch /payments/change-plan-preview → show prorated
 * charge/credit → explicit "Confirm & switch" that POSTs /payments/change-plan.
 */
export default function ChangePlanModal({ currentTier, busy, onConfirm, onClose }) {
  const [selected, setSelected] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [error, setError] = useState("");

  const selectTier = async (tierId) => {
    setSelected(tierId);
    setPreview(null);
    setError("");
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/payments/change-plan-preview?tier=${tierId}`);
      setPreview(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Could not fetch a preview from Stripe. Try again.");
    } finally {
      setLoadingPreview(false);
    }
  };

  const options = TIER_OPTIONS.filter((t) => t.id !== currentTier);
  const selectedLabel = options.find((t) => t.id === selected)?.label || "";
  const amountDue = preview?.amount_due ?? 0;
  const isCredit = amountDue < 0;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="change-plan-modal"
    >
      <div className="bg-white max-w-md w-full p-6 shadow-2xl border-l-4 border-[#264839]">
        <h3 className="font-serif text-xl text-[#11211A] mb-2">Change your plan</h3>
        <p className="text-xs text-[#4A5550] mb-4 leading-relaxed">
          Pick a new tier and we'll show you exactly what Stripe will charge (or credit) today, prorated to the day.
        </p>

        <div className="space-y-2">
          {options.map((t) => (
            <button
              key={t.id}
              disabled={busy || loadingPreview}
              onClick={() => selectTier(t.id)}
              className={`w-full text-left p-3 border transition flex justify-between items-center disabled:opacity-50 ${selected === t.id ? "border-[#264839] bg-[#F0EFEA]" : "border-[#E8ECE9] hover:border-[#264839]"}`}
              data-testid={`change-plan-option-${t.id}`}
            >
              <span className="font-serif text-[15px] text-[#11211A]">{t.label}</span>
              <span className="text-xs text-[#264839] tabular-nums">{t.price}</span>
            </button>
          ))}
        </div>

        {loadingPreview && (
          <div className="mt-4 flex items-center gap-2 text-xs text-[#7D8C85]" data-testid="change-plan-preview-loading">
            <Loader2 className="w-3 h-3 animate-spin" /> Calculating proration…
          </div>
        )}

        {error && (
          <div className="mt-4 text-xs text-[#B35C4A] bg-[#FBEEE9] px-3 py-2" data-testid="change-plan-preview-error">
            {error}
          </div>
        )}

        {preview && !loadingPreview && (
          <div className="mt-4 border border-[#E8ECE9] p-4 bg-[#FAFAF7]" data-testid="change-plan-preview">
            <div className="tracking-overline text-[10px] text-[#7D8C85] mb-2">Proration preview</div>
            <div className="flex justify-between items-baseline">
              <span className="text-sm text-[#4A5550]">
                {isCredit ? "Credit to your account today" : "Charged to your card today"}
              </span>
              <span className={`font-serif text-2xl tabular-nums ${isCredit ? "text-[#264839]" : "text-[#11211A]"}`} data-testid="change-plan-preview-amount">
                {fmtCents(amountDue, preview.currency)}
              </span>
            </div>
            {preview.prorations?.length > 0 && (
              <ul className="mt-3 space-y-1 text-[11px] text-[#7D8C85]" data-testid="change-plan-preview-lines">
                {preview.prorations.map((l, i) => (
                  <li key={i} className="flex justify-between gap-2">
                    <span className="truncate">{l.description || "Proration"}</span>
                    <span className="tabular-nums flex-shrink-0">{fmtCents(l.amount, preview.currency)}</span>
                  </li>
                ))}
              </ul>
            )}
            {preview.next_payment_attempt && (
              <div className="mt-3 text-[11px] text-[#7D8C85]">
                Next full charge on {new Date(preview.next_payment_attempt * 1000).toISOString().split("T")[0]}.
              </div>
            )}
          </div>
        )}

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="text-xs uppercase tracking-widest text-[#7D8C85] hover:text-[#11211A]" data-testid="change-plan-close">
            Close
          </button>
          <button
            disabled={!preview || busy || loadingPreview}
            onClick={() => onConfirm(selected)}
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 bg-[#264839] hover:bg-[#1B3529] text-white transition disabled:opacity-50"
            data-testid="change-plan-confirm"
          >
            {busy ? "Switching…" : selectedLabel ? `Confirm & switch to ${selectedLabel}` : "Confirm & switch"}
          </button>
        </div>
      </div>
    </div>
  );
}
